/**
 * What a rewrite replaces, and whose words a rewritten node carries.
 *
 * A rewrite is the one press that deletes reasoning on purpose: `reviseNode`
 * drops every claim below the node and re-derives from the reader's words, and
 * the connection candidates with an end among those claims go with them. The
 * study watched a reader rewrite a claim whose four children held the sharpest
 * insight in her graph; they were replaced, and the Overview's connections fell
 * from four to two, and nothing on screen had said either would happen. So the
 * count is said on the press, before, and in the toast, after — from this one
 * reading, which walks the graph the way the store's `dropSubtree` does, so the
 * number promised is the number removed (`engine/reasonRewrite.test.ts` holds
 * the two together).
 *
 * The other half is attribution. A rewrite keeps the node's model stamp — it
 * records who drafted the text the reader replaced (invariant 3) — and
 * `editedAt` says the words are the reader's now. Every surface that names a
 * rewritten node's author says both halves, in one phrasing, from here.
 *
 * Pure, and a leaf beside the types it reads.
 */

import { bridgeTouches, type ReasonNode, type Session } from './types'
import { STATUS_WORD } from './vocabulary'

export interface RewriteLoss {
  /** Every claim below the node: `reviseNode` drops each child's whole subtree. */
  claims: number
  /** Connection candidates (`session.bridges`, the Overview's pairs) with a local
   *  end among those claims — the store drops them with the claims. */
  connections: number
  /** A panel reading on the node itself: it ruled on the words being replaced,
   *  and the rewrite clears it. */
  panel: boolean
}

/** What rewriting this node would take off the board — read before the press. */
export function rewriteLoss(session: Session, nodeId: string): RewriteLoss {
  const node = session.nodes[nodeId]
  if (!node) return { claims: 0, connections: 0, panel: false }
  // `dropSubtree`'s own walk, from each child: parent pointers only. A synthesis
  // hanging elsewhere with a second parent in here is not dropped by it, so it
  // is not counted here either.
  const kids = new Map<string, string[]>()
  for (const id of session.order) {
    const p = session.nodes[id]?.parentId
    if (!p) continue
    const list = kids.get(p)
    if (list) list.push(id)
    else kids.set(p, [id])
  }
  const doomed = new Set<string>()
  const stack = [...(kids.get(nodeId) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    if (doomed.has(id) || !session.nodes[id]) continue
    doomed.add(id)
    for (const c of kids.get(id) ?? []) stack.push(c)
  }
  return {
    claims: doomed.size,
    connections: session.bridges.filter((b) => bridgeTouches(b, doomed)).length,
    panel: node.panel !== undefined,
  }
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`

/** "4 claims and 2 connection candidates" — or null when the rewrite replaces
 *  nothing but the words. */
export function rewriteLossPhrase(loss: RewriteLoss): string | null {
  const parts = [
    loss.claims ? plural(loss.claims, 'claim', 'claims') : null,
    loss.connections
      ? plural(loss.connections, 'connection candidate', 'connection candidates')
      : null,
    loss.panel ? 'the panel’s reading' : null,
  ].filter((p): p is string => p !== null)
  if (!parts.length) return null
  return parts.length === 1
    ? parts[0]!
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** The rewrite editor's priced press, with what it replaces on its face. */
export function rewritePressLabel(loss: RewriteLoss): string {
  const lost = rewriteLossPhrase(loss)
  return lost ? `Re-derive from here · replaces ${lost}` : 'Re-derive from here'
}

/**
 * The toast once the re-derivation has landed: what came in, what it replaced,
 * and the way back. Said even when the pass landed nothing — that is the case
 * where the replaced claims matter most, and the one a reader is least likely
 * to go looking for.
 */
export function rewriteLandedSentence(label: string, loss: RewriteLoss, produced: number): string {
  const lost = rewriteLossPhrase(loss)
  const head = `Rewrote “${label}”`
  if (!lost) {
    return produced
      ? `${head} — ${plural(produced, 'claim', 'claims')} re-derived from your wording. ⌘Z restores the previous wording.`
      : `${head}; the re-derivation added nothing. ⌘Z restores the previous wording.`
  }
  return produced
    ? `${head} — ${plural(produced, 'new claim', 'new claims')} replaced ${lost}. ⌘Z restores them.`
    : `${head}; the re-derivation added nothing, and what it replaced — ${lost} — is off the board. ⌘Z restores it.`
}

/**
 * "Rewritten by you · first proposed by Claude Opus 5" — both halves of a
 * rewritten node's authorship, or null for a node nobody rewrote. `opening`
 * false lowercases the status word for a line it does not open; the model's
 * name keeps its own casing either way.
 */
export function rewriteAuthorship(n: ReasonNode, opening = true): string | null {
  if (n.editedAt === undefined) return null
  const who = opening ? STATUS_WORD.revisedByYou : STATUS_WORD.revisedByYou.toLowerCase()
  return n.model ? `${who} · first proposed by ${n.model.name}` : who
}

/** A claim's three self-ratings, the model's own numbers. */
export interface SelfRatings {
  confidence: number
  novelty: number
  tension: number
}

/**
 * The model's self-ratings of a claim, or null where the reader has rewritten
 * it. A rewrite replaces the claim and re-derives BELOW it — it never rates the
 * reader's words — so the stored numbers are the model's reading of wording
 * that is no longer there. The export dropped them from the start; the card's
 * stat grid went on printing them. Every surface that prints them asks here —
 * the card, the report's meta line, the handoff's markers and the check's
 * "rated before searching" — so none keeps numbers another has dropped.
 *
 * Null too on a node carried from a conjecture (`carriedFrom`): its numbers are
 * placeholders no model gave, which every reader that grades or compares a
 * self-rating skips — asked here, so no surface has to remember to.
 */
export function selfRatingsOf(
  n: Pick<ReasonNode, 'editedAt' | 'carriedFrom' | 'confidence' | 'novelty' | 'tension'>,
): SelfRatings | null {
  if (n.editedAt !== undefined || n.carriedFrom !== undefined) return null
  return { confidence: n.confidence, novelty: n.novelty, tension: n.tension }
}

/**
 * A rating as every surface prints it: "0.75". Two decimals on the 0 to 1 scale
 * the model rates on, the record stores, and every export, digest and form
 * already printed — the self-ratings, a check's re-rating of novelty, the
 * confidence a result was recorded against.
 *
 * One formatter because the card printed two scales for one number: its stat
 * cell said NOVELTY 75 while the prior-art line directly under it said "novelty
 * re-rated 0.75 → 0.30", and the Overview's row said "novelty 75" beside that
 * same line and "66% confidence" under it. The hundredths were the odd ones out,
 * and the percent sign was the one thing the briefing tells a reader never to
 * make of these (a sort order, not a probability) — so the card and the row moved
 * to the record's scale, and a number printed through here cannot leave it.
 */
export const fmtRating = (x: number): string => x.toFixed(2)

/**
 * A claim nobody rated, as the canvas draws it: the middle of the size scale, a
 * strand of middle weight, and no bend toward the contradiction hue. With
 * `drawnGlowOf`'s zero, no novelty bloom either. Every channel says as little as
 * it can — the middle where the channel is a magnitude, and nothing where it
 * crosses a threshold, since a halo or a bend at any strength is the canvas
 * asserting a novelty or a friction the reader's words were never rated for.
 */
export const UNRATED_DRAWN: Readonly<SelfRatings> = { confidence: 0.5, novelty: 0.5, tension: 0 }

/**
 * What the flat canvas, the 3D view, their captions and the minimap size, weigh
 * and bend a claim by: its self-ratings, or `UNRATED_DRAWN` for a rewrite —
 * `selfRatingsOf`'s rule. The card, the exports and the Overview stopped printing
 * a rewritten claim's ratings (D204) while both renderers went on drawing them:
 * the reader's own sentence, sized and haloed by the model's reading of the words
 * it replaced. The node itself is returned where it is rated, because it carries
 * the three fields and this is read per node per frame, where nothing allocates.
 */
export function drawnRatingsOf(
  n: Pick<ReasonNode, 'editedAt' | 'confidence' | 'novelty' | 'tension'>,
): Readonly<SelfRatings> {
  return n.editedAt === undefined ? n : UNRATED_DRAWN
}

/** How strongly the canvas blooms a claim: its novelty, and nothing for a claim
 *  with no rating to bloom by (`drawnRatingsOf`). */
export const drawnGlowOf = (n: Pick<ReasonNode, 'editedAt' | 'novelty'>): number =>
  n.editedAt === undefined ? n.novelty : 0

/**
 * When the wording now on the node was written: a rewrite re-authors the claim,
 * so `editedAt` wherever it exists, and otherwise when the node arrived. Every
 * "reasoned before" comparison against a later record reads this — the coach's
 * cues, the taint walk, the evidence-change walk and the canvas's detached
 * strand — because the question each asks is whether whoever wrote these words
 * could have known the record, and the reader who rewrote a claim after it
 * landed could.
 */
export const writtenAt = (n: Pick<ReasonNode, 'editedAt' | 'createdAt'>): number =>
  n.editedAt ?? n.createdAt

/** Whether the reader rewrote this node after a moment — an entry in the thread
 *  about it, a pass that ran on it or produced it. */
export const rewrittenSince = (n: Pick<ReasonNode, 'editedAt'>, at: number): boolean =>
  n.editedAt !== undefined && n.editedAt > at

/** The mark a thread entry carries when the node it is about has been rewritten
 *  since: its words there are not the words on the node now. */
export const SINCE_REWRITTEN = `(since ${STATUS_WORD.revisedByYou.toLowerCase()})`
